/**
 * @author        calvin@zumper.com (Calvin)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   Loadable components for ModalWrapper
 */

import loadable from '@loadable/component'
import { createLoadableSection } from '@zcode/lazy-loadable-section'
import { ModalFallback } from '@zcode/nr/es/shared/components/Fallback'
import { MODAL_TYPE } from '@zcode/state-type'
import React from 'react'
import { logger } from '../../../logger'

const onLoadingError = (error: unknown) => {
  logger.error('loading error for message modal', error)
}

const loadFn = /* #__LOADABLE__ */ async () => {
  return await import(
    /* webpackChunkName: "MessageModalContainer" */ '../../../components/message/MessageModalContainer'
  )
}

const message = `Sorry, we couldn't load the message form...`
const handleReload = async () => await MessageModalComponent.reload()

const MessageModalErrorFallback = () => (
  <ModalFallback message={message} onClickReload={handleReload} />
)

const options = {
  errorFallback: MessageModalErrorFallback,
  loadingFallback: () => null,
  onLoadingError,
}

const MessageModalComponent = createLoadableSection(loadFn, options)

export const FlagModal = {
  type: MODAL_TYPE.FLAG,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "FlagModal" */ '../../../components/flag/FlagModalContainer'
      )
  ),
}
export const HashtagModal = {
  type: MODAL_TYPE.HASHTAG,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "HashtagModal" */ '../../../components/HashtagModal/HashtagModalContainer'
      )
  ),
}
export const InactiveSaveSearchModal = {
  type: MODAL_TYPE.INACTIVE_SAVE_SEARCH,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "InactiveSaveSearchModal" */ '../../../components/SaveSearch/InactiveSaveSearchSection/InactiveSaveSearchModal/InactiveSaveSearchModalContainer'
      )
  ),
}

export const MessageModal = {
  type: MODAL_TYPE.MESSAGE,
  component: MessageModalComponent,
}

export const MultiMessageModal = {
  type: MODAL_TYPE.MULTI_MESSAGE,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "MultiMessageModal" */ '../../../components/message/MultiMessageModal'
      )
  ),
}

export const SavedSearchOptionModal = {
  type: MODAL_TYPE.SAVED_SEARCH_OPTION,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "SavedSearchOptionModal" */ '../../../components/SavedSearchOptionModal/SavedSearchOptionModalContainer'
      )
  ),
}
export const ResidentQuotesModal = {
  type: MODAL_TYPE.RESIDENT_QUOTES,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "ResidentQuotesModal" */ '../../../components/ResidentQuotesModal/ResidentQuotesModal/ResidentQuotesModalContainer'
      )
  ),
}
export const SaveSearchModal = {
  type: MODAL_TYPE.SAVE_SEARCH,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "SaveSearchModal" */ '../../../components/SaveSearch/SaveSearchModalContainer'
      )
  ),
}

export const PriceDataTableModal = {
  type: MODAL_TYPE.PRICE_DATA_TABLE,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "PriceDataTableModal" */ '../../../components/PriceChart/PriceDataTable/PriceDataTableContainer'
      )
  ),
}

export const ShortTermAvailabilityModal = {
  type: MODAL_TYPE.SHORT_TERM_AVAILABILITY,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "ShortTermAvailabilityModal" */ '../../../components/detail/ShortTermContent/ShortTermAvailability/ShortTermAvailabilityModal'
      )
  ),
}

export const ShortTermAvailabilityFiltersModal = {
  type: MODAL_TYPE.SHORT_TERM_AVAILABILITY_V1,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "ShortTermAvailabilityFiltersModal" */ '../../../components/detail/ShortTermContent/ShortTermAvailability/ShortTermAvailabilityFiltersModal'
      )
  ),
}

export const RatingsAndReviewsModal = {
  type: MODAL_TYPE.RATINGS_AND_REVIEWS,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "RatingsAndReviewsModal" */ '../../../components/detail/RatingsAndReviews/RatingsAndReviewsModal'
      )
  ),
}

export const RatingsAndReviewsModalMobile = {
  type: MODAL_TYPE.RATINGS_AND_REVIEWS_MOBILE,
  component: loadable(
    async () =>
      await import(
        /* webpackChunkName: "RatingsAndReviewsModalMobile" */ '../../../components/detail/RatingsAndReviews/RatingsAndReviewsModalMobile'
      )
  ),
}

export const modals = [
  FlagModal,
  HashtagModal,
  InactiveSaveSearchModal,
  MessageModal,
  MultiMessageModal,
  PriceDataTableModal,
  ResidentQuotesModal,
  RatingsAndReviewsModal,
  RatingsAndReviewsModalMobile,
  SavedSearchOptionModal,
  SaveSearchModal,
  ShortTermAvailabilityModal,
  ShortTermAvailabilityFiltersModal,
]
